import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import { queryKeys } from "@/lib/queryKeys";
import { routes } from "@/routes";
import { useAuth } from "@/contexts/AuthContext";
import { HeroCarousel } from "@/components/layout/HeroCarousel";
import { AddPurchaseButton } from "@/components/product/AddPurchaseButton";
import { Price } from "@/components/product/Price";
import { ProductCarousel } from "@/components/product/ProductCarousel";
import { ShareButton } from "@/components/product/ShareButton";
import { Card } from "@/components/ui/Card";

export function HomePage() {
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.home,
    queryFn: api.catalog.getHome,
  });

  return (
    <>
      <HeroCarousel banners={data?.banners ?? []} loading={isLoading} />

      {/* Sobe 60px para o conteúdo montar sobre o banner. Descontado o `pt-5`
          (20px), sobram 40px de sobreposição visível. O `relative z-10` é o que
          garante que os cards pintem por cima do banner, não por baixo. */}
      <div className="relative z-10 mx-auto -mt-[60px] flex max-w-[1200px] flex-col gap-5 px-4 py-5">
        {isError ? (
          <Card className="p-8 text-center">
            <p className="text-sm text-ink-soft">
              Não conseguimos carregar as ofertas agora.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 text-sm font-semibold text-brand hover:underline"
            >
              Tentar de novo
            </button>
          </Card>
        ) : (
          <>
            {/* Primeira faixa: oferta do dia à esquerda, carrossel de ofertas à direita. */}
            <div className="grid gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
              {isLoading ? (
                <Card className="h-[380px] p-4">
                  <div className="skeleton size-full rounded" />
                </Card>
              ) : (
                data?.dailyOffer && (
                  <DailyOfferCard
                    label={data.dailyOffer.label}
                    product={data.dailyOffer.product}
                    userName={user?.displayName}
                  />
                )
              )}

              {/* `lg:self-center` faz o card ter a altura do próprio conteúdo e
                  ficar centralizado em relação ao da oferta do dia, que é mais
                  alto. Sem isso o grid o esticaria e sobraria uma faixa branca
                  no rodapé. */}
              <Card className="flex min-w-0 flex-col p-4 lg:self-center">
                <ProductCarousel
                  columns={4}
                  title="Ofertas"
                  loading={isLoading}
                  products={data?.shelves[0]?.products ?? []}
                  action={
                    <Link
                      to={data?.shelves[0]?.seeAllHref ?? routes.search()}
                      className="text-[13px] text-brand hover:underline"
                    >
                      {data?.shelves[0]?.seeAllLabel ?? "Ver todas as ofertas"}
                    </Link>
                  }
                />
              </Card>
            </div>

            {/* Demais prateleiras, uma por categoria. */}
            {(isLoading ? [] : (data?.shelves.slice(1) ?? [])).map((shelf) => (
              <Card key={shelf.id} className="p-4">
                <ProductCarousel
                  title={shelf.title}
                  products={shelf.products}
                  action={
                    <Link
                      to={shelf.seeAllHref}
                      className="text-[13px] text-brand hover:underline"
                    >
                      {shelf.seeAllLabel}
                    </Link>
                  }
                />
              </Card>
            ))}

            {isLoading && (
              <Card className="p-4">
                <ProductCarousel
                  title="Mais vendidos da semana"
                  loading
                  products={[]}
                />
              </Card>
            )}
          </>
        )}

        <p className="mx-auto max-w-2xl px-4 py-4 text-center text-[13px] leading-relaxed text-ink-soft">
          Não encontrou o que procurava?! Procure o item desejado{" "}
          <Link
            to={routes.search()}
            className="font-semibold text-brand hover:underline"
          >
            clicando aqui
          </Link>
          , finalize a compra em até 24 horas e envie para a gente o link do
          produto adquirido. Assim, você já poderá concorrer!
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

interface DailyOfferCardProps {
  label?: string | null;
  product: import("@/api").ProductSummary;
  userName?: string;
}

/** Card grande da esquerda: "Oferta do dia {nome do usuário}". */
function DailyOfferCard({ label, product, userName }: DailyOfferCardProps) {
  const path = routes.product(product.slug);
  // Personaliza com o nome de quem está logado; sem sessão, cai no rótulo do
  // backend e, na falta dele, no título simples.
  const suffix = userName ?? label ?? "";

  return (
    <Card className="group relative flex flex-col p-4">
      <h2 className="text-[15px] font-semibold text-ink lg:text-[17px]">
        Oferta do dia{suffix && ` ${suffix}`}
      </h2>

      <div className="absolute right-6 top-16 z-10">
        <AddPurchaseButton
          productId={product.id}
          productTitle={product.title}
        />
      </div>

      <Link to={path} className="mt-4 flex flex-1 flex-col gap-4">
        <div className="aspect-square w-full overflow-hidden">
          {product.images[0] ? (
            <img
              src={product.images[0]}
              alt={product.title}
              className="size-full object-contain transition-transform duration-200 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="size-full rounded bg-surface-muted" />
          )}
        </div>

        <h3 className="line-clamp-2 h-[2lh] text-sm leading-tight text-ink-soft transition-colors group-hover:text-brand">
          {product.title}
        </h3>

        <div className="mt-auto">
          <Price
            value={product.price}
            originalValue={product.originalPrice}
            discountPercent={product.discountPercent}
            size="xl"
          />
        </div>
      </Link>

      <div className="mt-3 flex justify-end">
        <ShareButton title={product.title} path={path} />
      </div>
    </Card>
  );
}
